/**
 * Returns MUI Data Grid localeText from i18n for the current language.
 * Use: <DataGrid localeText={useDataGridLocaleText()} ... />
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export default function useDataGridLocaleText() {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      toolbarColumns: t('dataGrid.toolbarColumns'),
      toolbarColumnsLabel: t('dataGrid.toolbarColumnsLabel'),
      toolbarFilters: t('dataGrid.toolbarFilters'),
      toolbarFiltersLabel: t('dataGrid.toolbarFiltersLabel'),
      toolbarFiltersTooltipHide: t('dataGrid.toolbarFiltersTooltipHide'),
      toolbarFiltersTooltipShow: t('dataGrid.toolbarFiltersTooltipShow'),
      toolbarFiltersTooltipActive: (count) =>
        t('dataGrid.toolbarFiltersTooltipActive', { count }),
      toolbarExport: t('dataGrid.toolbarExport'),
      toolbarExportLabel: t('dataGrid.toolbarExportLabel'),
      toolbarExportCSV: t('dataGrid.toolbarExportCSV'),
      toolbarExportPrint: t('dataGrid.toolbarExportPrint'),
      toolbarExportExcel: t('dataGrid.toolbarExportExcel'),
      columnsManagementSearchTitle: t('dataGrid.columnsManagementSearchTitle'),
      columnsManagementNoColumns: t('dataGrid.columnsManagementNoColumns'),
      columnsManagementShowHideAllText: t('dataGrid.columnsManagementShowHideAllText'),
      columnsManagementReset: t('dataGrid.columnsManagementReset'),
      columnsManagementDeleteIconLabel: t('dataGrid.columnsManagementDeleteIconLabel'),
      filterPanelAddFilter: t('dataGrid.filterPanelAddFilter'),
      filterPanelRemoveAll: t('dataGrid.filterPanelRemoveAll'),
      filterPanelDeleteIconLabel: t('dataGrid.filterPanelDeleteIconLabel'),
      filterPanelLogicOperator: t('dataGrid.filterPanelLogicOperator'),
      filterPanelOperator: t('dataGrid.filterPanelOperator'),
      filterPanelOperatorAnd: t('dataGrid.filterPanelOperatorAnd'),
      filterPanelOperatorOr: t('dataGrid.filterPanelOperatorOr'),
      filterPanelColumns: t('dataGrid.filterPanelColumns'),
      filterPanelInputLabel: t('dataGrid.filterPanelInputLabel'),
      filterPanelInputPlaceholder: t('dataGrid.filterPanelInputPlaceholder'),
      filterOperatorContains: t('dataGrid.filterOperatorContains'),
      filterOperatorEquals: t('dataGrid.filterOperatorEquals'),
      filterOperatorStartsWith: t('dataGrid.filterOperatorStartsWith'),
      filterOperatorEndsWith: t('dataGrid.filterOperatorEndsWith'),
      filterOperatorIsEmpty: t('dataGrid.filterOperatorIsEmpty'),
      filterOperatorIsNotEmpty: t('dataGrid.filterOperatorIsNotEmpty'),
      noRowsLabel: t('dataGrid.noRowsLabel'),
      noResultsOverlayLabel: t('dataGrid.noResultsOverlayLabel'),
      paginationRowsPerPage: t('dataGrid.paginationRowsPerPage'),
      paginationDisplayedRows: ({ from, to, count }) =>
        t('dataGrid.paginationDisplayedRows', { from, to, count }),
      paginationItemAriaLabel: (type) => {
        const key =
          type === 'first'
            ? 'paginationFirstPage'
            : type === 'last'
              ? 'paginationLastPage'
              : type === 'next'
                ? 'paginationNextPage'
                : 'paginationPreviousPage';
        return t(`dataGrid.${key}`);
      },
      columnMenuLabel: t('dataGrid.columnMenuLabel'),
      columnMenuShowColumns: t('dataGrid.columnMenuShowColumns'),
      columnMenuManageColumns: t('dataGrid.columnMenuManageColumns'),
      columnMenuFilter: t('dataGrid.columnMenuFilter'),
      columnMenuHideColumn: t('dataGrid.columnMenuHideColumn'),
      columnMenuSortAsc: t('dataGrid.columnMenuSortAsc'),
      columnMenuSortDesc: t('dataGrid.columnMenuSortDesc'),
      noColumnsOverlayManageColumns: t('dataGrid.noColumnsOverlayManageColumns'),
      footerRowSelected: (count) =>
        count === 1
          ? t('dataGrid.footerRowSelected_one', { count })
          : t('dataGrid.footerRowSelected_other', { count }),
    }),
    [t],
  );
}
